// The radial order menu (spec Addendum A). It is opened by a right-press on a friendly unit, centred on
// that unit's symbol, and carries every order card except the FRAGO: wedges grouped into one contiguous
// arc per family, coloured and shape-marked as on the deck, with the card's issue cost on the rim.
//
// The menu draws; it does not decide. Which wedge is lit, and what a release means, come from the
// gesture controller through `radialController` (src/ui/radial/state.ts), so a drag out from the centre
// and a click on a wedge select by exactly the same rule.

import type { CSSProperties } from 'react';
import { fmtDur } from '../cards/ui';
import {
  RADIAL_BAND_IN, RADIAL_BAND_OUT, RADIAL_COST_R, RADIAL_HALF, RADIAL_INNER, RADIAL_LABEL_R,
  RADIAL_MARK_R, RADIAL_OUTER, arcPath, labelRotation, ringPath, type FamilyShape,
} from './layout';
import { radialController, useRadial, type RadialWedge } from './state';

const FULL_SIZE = RADIAL_HALF * 2;

export function RadialMenu() {
  const open = useRadial((s) => s.open);
  const center = useRadial((s) => s.center);
  const anchor = useRadial((s) => s.anchor);
  const scale = useRadial((s) => s.scale);
  const wedges = useRadial((s) => s.wedges);
  const hover = useRadial((s) => s.hover);
  const callsign = useRadial((s) => s.callsign);
  const sticky = useRadial((s) => s.sticky);

  if (!open || !center) return null;
  const hovered = wedges.find((w) => w.card.id === hover) ?? null;
  // The menu may have been reflowed smaller to fit between the rails; the geometry is unchanged and the
  // SVG box is simply drawn at that scale, so the wedge the pointer is over still matches what is lit.
  const size = FULL_SIZE * scale;
  const half = RADIAL_HALF * scale;
  const off = anchor ? { x: (anchor.x - center.x) / scale, y: (anchor.y - center.y) / scale } : { x: 0, y: 0 };
  const leader = Math.hypot(off.x, off.y) > 6 ? off : null;

  // A real box centred on the unit, rather than a zero-size anchor: the menu has to be measurable (and
  // therefore visible to a test) and the hint below it needs somewhere to sit.
  const root: CSSProperties = {
    position: 'absolute',
    left: center.x - half,
    top: center.y - half,
    width: size,
    height: size,
    zIndex: 15,
    pointerEvents: 'none',
  };

  return (
    <div data-testid="radial-menu" data-sticky={sticky ? 'true' : 'false'} style={root}>
      <svg
        className="fade-in"
        width={size}
        height={size}
        viewBox={`${-RADIAL_HALF} ${-RADIAL_HALF} ${FULL_SIZE} ${FULL_SIZE}`}
        style={{
          position: 'absolute', left: 0, top: 0, overflow: 'visible',
          // While the press is still down the gesture controller owns the pointer; once it is released
          // in the dead zone the menu waits for a click, and the wedges take their own events.
          pointerEvents: sticky ? 'auto' : 'none',
        }}
      >
        {/* A panel behind the wedges: the terrain is busy, and a menu you have to hunt for is not a menu. */}
        <circle cx={0} cy={0} r={RADIAL_BAND_OUT + 2} fill="var(--color-slate-900)" fillOpacity={0.97} />
        <circle
          cx={0} cy={0} r={RADIAL_BAND_OUT + 2}
          fill="none" stroke="var(--color-slate-700)" strokeWidth={1}
        />
        {leader && (
          // The unit sat too close to an edge for the whole menu to fit, so the menu was pulled inboard;
          // this says which symbol it belongs to.
          <line
            x1={0} y1={0} x2={leader.x} y2={leader.y}
            stroke="var(--color-friendly)" strokeWidth={1} strokeOpacity={0.7} strokeDasharray="4 3"
          />
        )}
        {wedges.map((w) => (
          <Wedge key={w.card.id} w={w} lit={hover === w.card.id} />
        ))}
        {/* The family band: one arc per family, so the grouping reads before any single label does. */}
        {familyBands(wedges).map((b) => (
          <g key={b.family}>
            <path d={arcPath(RADIAL_BAND_IN, RADIAL_BAND_OUT, b.start + 0.02, b.end - 0.02)} fill={b.color} fillOpacity={0.75} />
            <path id={`radial-band-${b.family}`} d={ringPath((RADIAL_BAND_IN + RADIAL_BAND_OUT) / 2, b.start + 0.02, b.end - 0.02)} fill="none" />
          </g>
        ))}
        {/* The hub: the unit this menu belongs to, and the dead zone a release closes on. */}
        <circle cx={0} cy={0} r={RADIAL_INNER - 4} fill="var(--color-slate-900)" stroke="var(--color-friendly)" strokeWidth={1.5} />
        <text
          x={0} y={3.5} textAnchor="middle"
          style={{ font: '600 10px IBM Plex Mono, ui-monospace, monospace', fill: 'var(--color-friendly)', letterSpacing: '0.06em' }}
        >
          {callsign}
        </text>
      </svg>

      <div
        data-testid="radial-hint"
        className="mono"
        style={{
          position: 'absolute',
          left: 0,
          top: (RADIAL_HALF + RADIAL_BAND_OUT) * scale + 12,
          width: size,
          textAlign: 'center',
          pointerEvents: 'none',
          fontSize: 10,
          lineHeight: 1.35,
          letterSpacing: '0.06em',
          color: hovered?.reason ? 'var(--color-hostile)' : 'var(--color-paper)',
          textShadow: '0 0 6px var(--color-ink), 0 0 2px var(--color-ink)',
        }}
      >
        {hovered
          ? hovered.reason ?? `${hovered.label} — issue ${fmtDur(hovered.card.issueSec)}, exec ${fmtDur(hovered.card.execSec)}`
          : 'drag out to an order — release in the middle, or Esc, to close'}
      </div>
    </div>
  );
}

function Wedge({ w, lit }: { w: RadialWedge; lit: boolean }) {
  const disabled = w.reason !== undefined;
  const { deg, flip } = labelRotation(w.mid);
  const labelR = RADIAL_LABEL_R;
  const markR = RADIAL_MARK_R;
  const costR = RADIAL_COST_R;
  return (
    <g
      data-testid="radial-wedge"
      data-card={w.card.id}
      data-lit={lit ? 'true' : 'false'}
      aria-disabled={disabled ? 'true' : 'false'}
      aria-label={w.label}
      opacity={disabled ? 0.34 : 1}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      onPointerEnter={() => useRadial.setState({ hover: w.card.id })}
      onClick={() => radialController.choose(w.card.id)}
    >
      <title>{w.reason ?? `${w.label} — issue ${fmtDur(w.card.issueSec)} of command-net time`}</title>
      <path
        d={arcPath(RADIAL_INNER, RADIAL_OUTER, w.start, w.end)}
        fill={disabled ? 'var(--color-slate-700)' : w.color}
        fillOpacity={lit ? 0.5 : 0.16}
        stroke={disabled ? 'var(--color-slate-700)' : w.color}
        strokeOpacity={lit ? 1 : 0.7}
        strokeWidth={lit ? 1.8 : 1}
      />
      <g transform={`rotate(${deg.toFixed(2)}) translate(${markR},0)`}>
        <ShapeMark shape={w.shape} color={disabled ? 'var(--color-fog)' : w.color} />
      </g>
      <g transform={`rotate(${deg.toFixed(2)}) translate(${labelR},0) rotate(${flip ? 180 : 0})`}>
        <text
          x={0} y={3} textAnchor="middle"
          style={{
            font: `${lit ? 600 : 500} 9.5px IBM Plex Mono, ui-monospace, monospace`,
            fill: disabled ? 'var(--color-fog)' : lit ? 'var(--color-paper)' : w.color,
            letterSpacing: '0.05em',
          }}
        >
          {w.label}
        </text>
      </g>
      <g transform={`rotate(${deg.toFixed(2)}) translate(${costR},0) rotate(${flip ? 180 : 0})`}>
        <text
          x={0} y={3} textAnchor="middle"
          style={{ font: '400 8px IBM Plex Mono, ui-monospace, monospace', fill: 'var(--color-fog)' }}
        >
          {fmtDur(w.card.issueSec)}
        </text>
      </g>
    </g>
  );
}

/** The family's identity mark, the same five shapes the deck's cards carry (spec §8). */
function ShapeMark({ shape, color }: { shape: FamilyShape; color: string }) {
  const r = 5;
  switch (shape) {
    case 'circle':
      return <circle cx={0} cy={0} r={r} fill={color} />;
    case 'rectangle':
      return <rect x={-r} y={-r * 0.8} width={r * 2} height={r * 1.6} fill={color} />;
    case 'diamond':
      return <polygon points={`0,${-r} ${r},0 0,${r} ${-r},0`} fill={color} />;
    case 'hexagon':
      return <polygon points={`${-r / 2},${-r} ${r / 2},${-r} ${r},0 ${r / 2},${r} ${-r / 2},${r} ${-r},0`} fill={color} />;
    case 'chevron':
      return <polygon points={`${-r},${-r} ${r * 0.4},${-r} ${r},0 ${r * 0.4},${r} ${-r},${r} ${-r * 0.5},0`} fill={color} />;
  }
}

/** Collapses the wedge list back into one arc per family, for the band. */
function familyBands(wedges: RadialWedge[]): { family: string; start: number; end: number; color: string }[] {
  const out: { family: string; start: number; end: number; color: string }[] = [];
  for (const w of wedges) {
    const last = out[out.length - 1];
    if (last && last.family === w.card.family) {
      last.end = w.end;
      continue;
    }
    out.push({ family: w.card.family, start: w.start, end: w.end, color: w.color });
  }
  return out;
}
